import type { Session } from './storage';

export interface Recommendation { level: number; reason: string }

function daysBetween(a: string, b: string): number {
  return Math.abs(new Date(b).getTime() - new Date(a).getTime()) / 86_400_000;
}

export function recommendLevel(sessions: Session[], now = new Date()): Recommendation {
  const complete = sessions
    .filter((s) => Number.isFinite(new Date(s.completedAt).getTime()))
    .sort((a, b) => a.completedAt.localeCompare(b.completedAt));
  if (!complete.length) return { level: 1, reason: 'Starting with 2 rounds at the lower ranges. Build familiarity before adding work; one workout never triggers an increase.' };

  const recent = complete.slice(-6);
  if (recent.slice(-2).some((s) => s.effort === 'hard')) {
    const current = Math.max(1, Math.min(4, recent.at(-1)?.level ?? 1));
    return { level: Math.max(0, current - 1), reason: 'A recent session felt hard, so the next plan eases back. You stay in control.' };
  }

  const first = complete[0]!.completedAt;
  const span = daysBetween(first, now.toISOString());
  const successful = complete.filter((s) => s.effort === 'easy' || s.effort === 'right');

  // Three rounds require at least six successful completions over 14 days.
  if (successful.length >= 6 && span >= 14) {
    const threeRoundSuccesses = successful.filter((s) => s.level >= 3).length;
    if (threeRoundSuccesses >= 4 && span >= 21) {
      return { level: 4, reason: 'You have handled at least 4 three-round sessions across 3+ weeks. This adds reps gradually.' };
    }
    return { level: 3, reason: 'At least 6 comfortable completions across 14+ days support trying 3 rounds, with lower rep ranges.' };
  }

  // A modest rep/time increase still needs four successes across a full week.
  if (successful.length >= 4 && span >= 7) {
    return { level: 2, reason: 'At least 4 successful sessions across 7+ days support a small increase while keeping 2 rounds.' };
  }

  const needed = Math.max(0, 4 - successful.length);
  const dayText = span < 7 ? ` and ${Math.ceil(7 - span)} more day${Math.ceil(7 - span) === 1 ? '' : 's'}` : '';
  return {
    level: 1,
    reason: `Staying at 2 foundation rounds. Progress waits for ${needed || 'more'} successful session${needed === 1 ? '' : 's'}${dayText}; never after one workout.`,
  };
}

export function selectedLevel(sessions: Session[], manualLevel: number | null, now = new Date()): Recommendation {
  if (manualLevel !== null) {
    return { level: Math.max(0, Math.min(4, manualLevel)), reason: 'Manual choice is active. Automatic recommendations will resume if you select “Use recommendation.”' };
  }
  return recommendLevel(sessions, now);
}
